import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Tooltip,
  Snackbar,
  Alert,
  IconButton,
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { BRAND_COLORS, alpha } from '../../../../styles/theme';
import {
  FlightDetailsDialog,
  useFlightDetailsQuery,
  flightDetailsKeys,
} from '../../../flight-details';
import { useMaintenanceTimelineQuery } from '../statistics.queries';
import {
  collectYears,
  flightPositions,
  maintenancePositions,
  notePositions,
  type TimelineNote,
} from './timelinePositioning';
import {
  buildMonthMarkers,
  formatMaintenanceTooltip,
  formatNoteTooltip,
} from './tooltipFormatters';
import { useYearNavigation } from './useYearNavigation';
import { useTranslation } from '../../../../i18n';
import { sharedOrgList } from '../../../../utils/sharedFlights';

interface SimpleGanttChartProps {
  flightDates: string[];
  /** #1693: dates carrying a flight another organization shared → the sharers' names. */
  sharedFlightDates?: Record<string, string[]>;
  droneSerial: string;
  notes?: TimelineNote[];
  onNoteClick?: (noteId: string) => void;
  onYearChange?: (year: number) => void;
}

// Blue color for note markers.
const NOTES_COLOR = '#2196F3';

export const SimpleGanttChart: React.FC<SimpleGanttChartProps> = ({
  flightDates,
  sharedFlightDates,
  droneSerial,
  notes = [],
  onNoteClick,
  onYearChange,
}) => {
  const { t } = useTranslation();

  // Flight details dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedFlightDate, setSelectedFlightDate] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const queryClient = useQueryClient();

  // TanStack Query — maintenance timeline
  const { data: maintenanceRecords = [], isLoading: loading } =
    useMaintenanceTimelineQuery(droneSerial);

  // TanStack Query — flight details (enabled only when a date is selected)
  const { data: flightDetails = [], isLoading: loadingFlights } = useFlightDetailsQuery(
    droneSerial,
    selectedFlightDate
  );

  // Calculate all available years from all data sources
  const allYears = useMemo(
    () => collectYears(flightDates, maintenanceRecords, notes),
    [flightDates, maintenanceRecords, notes]
  );

  // Year navigation (selection + prev/next + boundary + parent notify)
  const { currentYear, handlePreviousYear, handleNextYear, isAtLatestYear } = useYearNavigation(
    allYears,
    onYearChange
  );

  // Positioned data for the current year
  const flightDatePositions = useMemo(
    () => flightPositions(flightDates, currentYear),
    [flightDates, currentYear]
  );
  const maintenanceBarPositions = useMemo(
    () => maintenancePositions(maintenanceRecords, currentYear),
    [maintenanceRecords, currentYear]
  );
  const noteMarkerPositions = useMemo(
    () => notePositions(notes, currentYear),
    [notes, currentYear]
  );
  const monthMarkers = useMemo(() => buildMonthMarkers(currentYear), [currentYear]);

  // Handler for flight tick click
  const handleFlightClick = (flightDate: string) => {
    setSelectedFlightDate(flightDate);
    setDialogOpen(true);
    setError(null);
  };

  // Handler for closing dialog
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedFlightDate(null);
    setError(null);
  };

  // Handler for flight data update - invalidate TanStack Query cache to re-fetch
  const handleFlightUpdated = () => {
    queryClient.invalidateQueries({
      queryKey: flightDetailsKeys.detail(droneSerial, selectedFlightDate),
    });
  };

  return (
    <Box sx={{ width: '100%', p: 2 }}>
      {/* Header with title and year navigation */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Timeline
        </Typography>

        {/* Year navigation */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            onClick={handlePreviousYear}
            size="small"
            aria-label="Previous year"
            sx={{ color: 'text.secondary' }}
          >
            <ChevronLeft />
          </IconButton>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              minWidth: '60px',
              textAlign: 'center',
              color: 'text.primary',
            }}
          >
            {currentYear}
          </Typography>
          <IconButton
            onClick={handleNextYear}
            size="small"
            aria-label="Next year"
            disabled={isAtLatestYear}
            sx={{ color: 'text.secondary' }}
          >
            <ChevronRight />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ mb: 1 }}>
        {/* OPS axis */}
        <Box sx={{ display: 'flex', alignItems: 'center', height: '40px', mb: 1 }}>
          <Typography
            variant="body2"
            sx={{
              minWidth: '60px',
              fontWeight: 500,
              color: BRAND_COLORS.forestGreen.main,
            }}
          >
            OPS
          </Typography>

          {/* Timeline bar */}
          <Box
            sx={{
              flex: 1,
              ml: 2,
              position: 'relative',
              height: '20px',
              backgroundColor: '#f0f0f0',
              borderRadius: '4px',
              border: `1px solid ${alpha(BRAND_COLORS.forestGreen.main, 0.3)}`,
            }}
          >
            {/* Flight date markers */}
            {flightDatePositions.map((flight, index) => {
              // A date with a flight another organization shared is marked in its
              // own colour and names the sharer (#1693).
              const sharers = sharedFlightDates?.[flight.date];
              const shared = sharers !== undefined;
              return (
                <Tooltip
                  key={index}
                  title={
                    shared
                      ? `Flight: ${flight.date} (click for details) · ${t(
                          'dronePageShared.timelineShared',
                          { orgs: sharedOrgList(t, sharers) }
                        )}`
                      : `Flight: ${flight.date} (click for details)`
                  }
                  arrow
                  placement="top"
                >
                  <Box
                    onClick={() => handleFlightClick(flight.date)}
                    data-shared={shared ? 'true' : undefined}
                    sx={{
                      position: 'absolute',
                      left: `${flight.position}%`,
                      top: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '4px',
                      height: '16px',
                      backgroundColor: shared
                        ? BRAND_COLORS.yellow.main
                        : BRAND_COLORS.forestGreen.main,
                      borderRadius: '2px',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: '#002B20', // Darker shade of forestGreen
                        transform: 'translate(-50%, -50%) scale(1.2)',
                      },
                    }}
                  />
                </Tooltip>
              );
            })}
          </Box>
        </Box>

        {/* MAINT axis */}
        <Box sx={{ display: 'flex', alignItems: 'center', height: '40px' }}>
          <Typography
            variant="body2"
            sx={{
              minWidth: '60px',
              fontWeight: 500,
              color: BRAND_COLORS.yellow?.main || '#FFB800',
            }}
          >
            MAINT
          </Typography>

          {/* Timeline bar */}
          <Box
            sx={{
              flex: 1,
              ml: 2,
              position: 'relative',
              height: '20px',
              backgroundColor: '#f0f0f0',
              borderRadius: '4px',
              border: `1px solid ${alpha(BRAND_COLORS.yellow?.main || '#FFB800', 0.3)}`,
            }}
          >
            {/* Loading indicator */}
            {loading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                }}
              >
                <CircularProgress size={16} />
              </Box>
            )}

            {/* Maintenance record bars */}
            {!loading &&
              maintenanceBarPositions.map((maintenance) => {
                return (
                  <Tooltip
                    key={maintenance.record.id}
                    title={formatMaintenanceTooltip(maintenance.record)}
                    arrow
                    placement="top"
                    componentsProps={{
                      tooltip: {
                        sx: {
                          backgroundColor: 'rgba(0, 0, 0, 0.9)',
                          fontSize: '0.75rem',
                          maxWidth: 200,
                        },
                      },
                      arrow: {
                        sx: {
                          color: 'rgba(0, 0, 0, 0.9)',
                        },
                      },
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        left: `${maintenance.startPosition}%`,
                        top: '2px',
                        width: `${maintenance.width}%`,
                        height: '16px',
                        backgroundColor: BRAND_COLORS.yellow?.main || '#FFB800',
                        borderRadius: '2px',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: '#E6B327', // Darker shade of yellow
                          transform: 'scaleY(1.2)',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        },
                      }}
                    />
                  </Tooltip>
                );
              })}
          </Box>
        </Box>

        {/* NOTES axis */}
        <Box sx={{ display: 'flex', alignItems: 'center', height: '40px', mt: 1 }}>
          <Typography
            variant="body2"
            sx={{
              minWidth: '60px',
              fontWeight: 500,
              color: NOTES_COLOR,
            }}
          >
            NOTES
          </Typography>

          {/* Timeline bar */}
          <Box
            sx={{
              flex: 1,
              ml: 2,
              position: 'relative',
              height: '20px',
              backgroundColor: '#f0f0f0',
              borderRadius: '4px',
              border: `1px solid ${alpha(NOTES_COLOR, 0.3)}`,
            }}
          >
            {/* Note markers */}
            {noteMarkerPositions.map((notePos, index) => (
              <Tooltip
                key={notePos.note.id || index}
                title={formatNoteTooltip(notePos.note, notePos.date)}
                arrow
                placement="top"
                componentsProps={{
                  tooltip: {
                    sx: {
                      backgroundColor: 'rgba(0, 0, 0, 0.9)',
                      fontSize: '0.75rem',
                      maxWidth: 250,
                    },
                  },
                  arrow: {
                    sx: {
                      color: 'rgba(0, 0, 0, 0.9)',
                    },
                  },
                }}
              >
                <Box
                  onClick={() => onNoteClick?.(notePos.note.id)}
                  sx={{
                    position: 'absolute',
                    left: `${notePos.position}%`,
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '4px',
                    height: '16px',
                    backgroundColor: NOTES_COLOR,
                    borderRadius: '2px',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: '#1565C0', // Darker shade of blue
                      transform: 'translate(-50%, -50%) scale(1.2)',
                    },
                  }}
                />
              </Tooltip>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Timeline axis with month markers */}
      <Box sx={{ ml: '76px', position: 'relative', height: '30px' }}>
        {/* Axis line */}
        <Box
          sx={{
            position: 'absolute',
            top: '0px',
            left: '0%',
            right: '0%',
            height: '1px',
            backgroundColor: '#ddd',
          }}
        />

        {/* Month markers */}
        {monthMarkers.map((marker, index) => (
          <Box
            key={index}
            sx={{
              position: 'absolute',
              left: `${marker.position}%`,
              transform: 'translateX(-50%)',
              top: '-2px',
            }}
          >
            <Box
              sx={{
                width: '1px',
                height: '6px',
                backgroundColor: '#999',
                mb: 0.5,
              }}
            />
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.7rem',
                color: '#666',
                whiteSpace: 'nowrap',
              }}
            >
              {marker.label}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Flight Details Dialog */}
      <FlightDetailsDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        flightDate={selectedFlightDate}
        droneSerial={droneSerial}
        flights={flightDetails}
        loading={loadingFlights}
        onFlightUpdated={handleFlightUpdated}
      />

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};
